import React, { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { STATUS_COLORS, DEFAULT_CONFIG } from '../utils/constants';
import { merchichConverter } from '../services/merchichService';
import { formatNumber, calculateResteAPayer, getZoneShortName } from '../utils/helpers';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to auto-fit bounds when projects change
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [map, bounds]);

  return null;
};

// Highlight selected marker
const HighlightMarker = ({ selectedProjectId, markersData }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedProjectId) {
      const selected = markersData.find(m => m.project.id === selectedProjectId);
      if (selected) {
        map.setView([selected.lat, selected.lng], Math.max(map.getZoom(), 14));
      }
    }
  }, [selectedProjectId, markersData, map]);

  return null;
};

const Map = ({ projects = [], onProjectSelect, height = '400px', selectedProjectId = null, canSeeFinance = true }) => {
  const mapRef = useRef(null);

  // Convert projects to markers data with WGS84 coordinates
  const markersData = useMemo(() => {
    const data = [];

    projects.forEach(project => {
      if (!project.coordonnees || project.coordonnees.length === 0 || !project.zoneMerchich) return;

      const firstPoint = project.coordonnees[0];
      const wgs84 = merchichConverter.merchichToWGS84(firstPoint.x, firstPoint.y, project.zoneMerchich);

      if (!wgs84) return;

      data.push({
        project,
        point: firstPoint,
        lat: wgs84.latitude,
        lng: wgs84.longitude,
        wgs84
      });
    });

    return data;
  }, [projects]);

  // Calculate bounds
  const bounds = useMemo(() => {
    if (markersData.length === 0) return null;

    const latLngs = markersData.map(m => [m.lat, m.lng]);
    return L.latLngBounds(latLngs);
  }, [markersData]);

  // Popup : résumé du dossier + bouton vers la fiche complète
  const createPopupContent = (project, point) => {
    const statut = project.statut || 'En cours';
    const statusColor = STATUS_COLORS[statut] || '#3B82F6';
    const resteAPayer = calculateResteAPayer(project.montantTotal, project.avances, project.fraisSupplementaires);
    const row = (label, value) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12px', padding: '3px 0' }}>
        <span style={{ color: '#64748B' }}>{label}</span>
        <span style={{ color: '#0F172A', fontWeight: 500, textAlign: 'right' }}>{value || '-'}</span>
      </div>
    );
    return (
      <div style={{ width: '250px', fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.client || 'Sans nom'}</div>
            <div style={{ fontSize: '11px', color: '#64748B' }}>Réf {project.refGlobal || '-'} · {statut}</div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '4px' }}>
          {row('Nature', project.natureProjet)}
          {row('Commune', project.commune)}
          {row('Agent', project.agentResponsable)}
          {row('Date', project.date)}
          {row('Point', `${point.nom || 'Point 1'} · ${getZoneShortName(project.zoneMerchich)}`)}
          {canSeeFinance && row('Montant', `${formatNumber(project.montantTotal || 0)} DH`)}
          {canSeeFinance && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '3px 0' }}>
              <span style={{ color: '#64748B' }}>Reste à payer</span>
              <span style={{ color: resteAPayer > 0 ? '#EA580C' : '#059669', fontWeight: 600 }}>{formatNumber(resteAPayer)} DH</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onProjectSelect && onProjectSelect(project)}
          style={{ marginTop: '8px', width: '100%', minHeight: '38px', border: 0, borderRadius: '10px', background: '#0284C7', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
        >
          Ouvrir le dossier →
        </button>
      </div>
    );
  };

  // Legend component
  const Legend = () => (
    <div className="absolute top-3 left-3 bg-white rounded-lg shadow-lg p-3 z-[400] text-xs">
      <h4 className="font-semibold text-slate-700 mb-2">Statuts</h4>
      <div className="space-y-1.5">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center">
            <span
              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-slate-600 truncate">{status}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative z-0 isolate rounded-lg overflow-hidden shadow-lg" style={{ height }}>
      <MapContainer
        ref={mapRef}
        center={[DEFAULT_CONFIG.MAP_CENTER.lat, DEFAULT_CONFIG.MAP_CENTER.lng]}
        zoom={DEFAULT_CONFIG.MAP_ZOOM}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        {/* Zoom control positioned on the right */}
        <ZoomControl position="topright" />
        {/* Satellite hybrid layer using ESRI */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution='Tiles &copy; Esri'
        />
        {/* Labels overlay for hybrid effect */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
          attribution=''
        />

        {/* Auto-fit bounds */}
        {bounds && <FitBounds bounds={bounds} />}

        {/* Highlight selected */}
        <HighlightMarker selectedProjectId={selectedProjectId} markersData={markersData} />

        {/* Markers */}
        {markersData.map((markerData, index) => {
          const { project, point, lat, lng } = markerData;
          const statut = project.statut || 'En cours';
          const statusColor = STATUS_COLORS[statut] || '#3B82F6';
          const isSelected = selectedProjectId === project.id;

          return (
            <CircleMarker
              key={project.id || index}
              center={[lat, lng]}
              radius={isSelected ? 12 : 8}
              pathOptions={{
                fillColor: statusColor,
                fillOpacity: 0.9,
                color: '#FFFFFF',
                weight: 2
              }}
            >
              <Popup closeButton={true} autoPan={true}>
                {createPopupContent(project, point)}
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>

      {/* Legend overlay */}
      {markersData.length > 0 && <Legend />}
    </div>
  );
};

export default Map;
